import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Activity,
  ArrowRight,
  BarChart3,
  CheckCircle,
  ClipboardList,
  Dna,
  Eye,
  EyeOff,
  FlaskConical,
  Lock,
  Mail,
  Moon,
  ShieldCheck,
  Sun,
  Users,
} from "lucide-react";
import { useSearchParams, useNavigate } from "react-router-dom";

import type { User } from "@/types/Sidebar.type";
import type { ApiError, PasswordExpiryInfo } from "@/types/auth.type";
import { ROLE_DISPLAY_CONFIG, UserRoleEnum } from "@/types";
import { CLINICAL_FONT_FAMILY } from "@/components/shared/constants";
import { container } from "@/container/DependencyContainer";
import { useThemeMode } from "@/components/shared/ThemeProvider";
import ForgotPasswordPage from "./ForgotPasswordPage";

// ─── Static data ─────────────────────────────────────────────────────────────

const FEATURE_LIST = [
  {
    key: "study-mgmt",
    Icon: ClipboardList,
    title: "Study Management",
    desc: "End-to-end clinical trial lifecycle with intake, activation & milestone tracking",
  },
  {
    key: "site-user",
    Icon: Users,
    title: "Site & User Management",
    desc: "Role-based access across 11 user roles with full RBAC enforcement",
  },
  {
    key: "analytics",
    Icon: BarChart3,
    title: "Analytics & RBQM",
    desc: "Risk-based quality management dashboards and real-time KPI monitoring",
  },
  {
    key: "compliance",
    Icon: ShieldCheck,
    title: "Compliance & Audit",
    desc: "Complete audit trails, eTMF management and regulatory-grade documentation",
  },
] as const;

const TRUST_BADGES = [
  { key: "soc2", Icon: ShieldCheck, label: "SOC 2 Compliant" },
  { key: "gcp", Icon: CheckCircle, label: "GCP Validated" },
  { key: "enc", Icon: Lock, label: "256-bit Encryption" },
] as const;

/** Marketing chips on the login hero — labels from ROLE_DISPLAY_CONFIG. */
const SUPPORTED_ROLES = [
  UserRoleEnum.TENANT,
  UserRoleEnum.PROJECT_MANAGER,
  UserRoleEnum.CRA,
  UserRoleEnum.PRINCIPAL_INVESTIGATOR,
  UserRoleEnum.STUDY_COORDINATOR,
  UserRoleEnum.DATA_MANAGER,
  UserRoleEnum.SPONSOR,
  UserRoleEnum.SITE_ADMIN,
  UserRoleEnum.CTA,
  UserRoleEnum.QA_QC,
].map((role) => ROLE_DISPLAY_CONFIG[role].name);

// ─── useLoginForm hook ────────────────────────────────────────────────────────

interface UseLoginFormReturn {
  email: string;
  setEmail: (v: string) => void;
  password: string;
  setPassword: (v: string) => void;
  rememberMe: boolean;
  setRememberMe: (v: boolean) => void;
  showPassword: boolean;
  setShowPassword: (v: boolean) => void;
  loading: boolean;
  error: string | null;
  snackbarOpen: boolean;
  closeSnackbar: () => void;
  handleSubmit: (e: React.FormEvent) => void;
}

function useLoginForm(
  onLogin: (user: User, passwordExpiry?: PasswordExpiryInfo) => void,
): UseLoginFormReturn {
  const navigate = useNavigate();
  const [email, setEmail] = useState(
    () => localStorage.getItem("rememberMeEmail") ?? "",
  );
  const [password, setPassword] = useState(
    () => localStorage.getItem("rememberMePassword") ?? "",
  );
  const [rememberMe, setRememberMe] = useState(
    () => localStorage.getItem("rememberMe") === "true",
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const persistRememberMe = (em: string, pw: string, remember: boolean) => {
    if (remember) {
      localStorage.setItem("rememberMeEmail", em);
      localStorage.setItem("rememberMePassword", pw);
      localStorage.setItem("rememberMe", "true");
    } else {
      localStorage.removeItem("rememberMeEmail");
      localStorage.removeItem("rememberMePassword");
      localStorage.removeItem("rememberMe");
    }
  };

  const submitLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      persistRememberMe(email, password, rememberMe);
      const response = await container.loginUseCase.execute({
        email,
        password,
      });
      onLogin(response.user, response.passwordExpiry);
    } catch (apiError) {
      const err = apiError as ApiError & Error;
      if (err.code === "PASSWORD_EXPIRED") {
        navigate(`/change-expired-password?email=${encodeURIComponent(email)}`);
        return;
      }
      setError(
        err.message ||
          "Login failed. Please check your credentials and try again.",
      );
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    rememberMe,
    setRememberMe,
    showPassword,
    setShowPassword,
    loading,
    error,
    snackbarOpen,
    closeSnackbar: () => setSnackbarOpen(false),
    handleSubmit: (e: React.FormEvent) => {
      void submitLogin(e);
    },
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const PageTopBar: React.FC<{ isDark: boolean; toggleTheme: () => void }> = ({
  isDark,
  toggleTheme,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      px: 3,
      py: 1.5,
      backdropFilter: "blur(12px)",
      backgroundColor: isDark
        ? "rgba(15,23,42,0.85)"
        : "rgba(255,255,255,0.85)",
      borderBottom: `1px solid ${isDark ? "rgba(51,65,85,0.4)" : "rgba(11,95,255,0.1)"}`,
      position: "sticky",
      top: 0,
      zIndex: 100,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          background: "linear-gradient(135deg, #0B5FFF 0%, #0FB6A6 100%)",
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 2px 8px rgba(11,95,255,0.35)",
        }}
      >
        <Activity size={20} color="white" />
      </Box>
      <Box>
        <Typography
          sx={{
            color: isDark ? "#F8FAFC" : "#0F172A",
            fontWeight: 700,
            fontFamily: CLINICAL_FONT_FAMILY,
            lineHeight: 1,
            fontSize: "1.1rem",
            letterSpacing: "-0.02em",
          }}
        >
          AIClinTrack
        </Typography>
        <Typography
          sx={{
            color: isDark ? "#64748B" : "#94A3B8",
            fontSize: "0.7rem",
            fontFamily: CLINICAL_FONT_FAMILY,
            letterSpacing: "0.05em",
          }}
        >
          CLINICAL TRIAL MANAGEMENT
        </Typography>
      </Box>
    </Box>

    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Chip
        label="v2.0.0"
        size="small"
        sx={{
          backgroundColor: isDark ? "rgba(11,95,255,0.15)" : "#EFF6FF",
          color: "#0B5FFF",
          fontSize: "0.7rem",
          fontWeight: 600,
          height: 22,
        }}
      />
      <Tooltip title={`Switch to ${isDark ? "light" : "dark"} mode`}>
        <IconButton
          onClick={toggleTheme}
          size="small"
          sx={{
            color: isDark ? "#94A3B8" : "#64748B",
            "&:hover": {
              backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "#F1F5F9",
            },
          }}
        >
          {isDark ? <Sun size={18} /> : <Moon size={18} />}
        </IconButton>
      </Tooltip>
    </Box>
  </Box>
);

const LeftMarketingPanel: React.FC<{ isDark: boolean }> = ({ isDark }) => (
  <Box
    sx={{
      display: { xs: "none", lg: "flex" },
      flex: "0 0 52%",
      flexDirection: "column",
      justifyContent: "center",
      position: "relative",
      overflow: "hidden",
      background: isDark
        ? "linear-gradient(145deg, #0B1E4A 0%, #0D2A5E 40%, #0A3D55 100%)"
        : "linear-gradient(145deg, #0B5FFF 0%, #0047CC 40%, #0FB6A6 100%)",
      px: 6,
      py: 8,
    }}
  >
    {/* Decorative background blobs */}
    <Box
      sx={{
        position: "absolute",
        top: "-10%",
        right: "-8%",
        width: 320,
        height: 320,
        borderRadius: "50%",
        background: "rgba(255,255,255,0.06)",
        filter: "blur(40px)",
      }}
    />
    <Box
      sx={{
        position: "absolute",
        bottom: "-8%",
        left: "-6%",
        width: 260,
        height: 260,
        borderRadius: "50%",
        background: "rgba(15,182,166,0.15)",
        filter: "blur(50px)",
      }}
    />

    {/* Floating decorative icons */}
    <Box
      sx={{
        position: "absolute",
        top: "8%",
        right: "10%",
        animation: "float 4s ease-in-out infinite",
        opacity: 0.25,
      }}
    >
      <Dna size={64} color="white" />
    </Box>
    <Box
      sx={{
        position: "absolute",
        bottom: "10%",
        right: "6%",
        animation: "float 5s ease-in-out infinite 1s",
        opacity: 0.2,
      }}
    >
      <FlaskConical size={52} color="white" />
    </Box>
    <Box
      sx={{
        position: "absolute",
        top: "55%",
        right: "3%",
        animation: "pulse 3s ease-in-out infinite",
        opacity: 0.2,
      }}
    >
      <Activity size={42} color="white" />
    </Box>

    {/* Main content */}
    <Box sx={{ position: "relative", zIndex: 1 }}>
      {/* Online status badge */}
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          px: 1.5,
          py: 0.5,
          backgroundColor: "rgba(255,255,255,0.15)",
          borderRadius: 10,
          border: "1px solid rgba(255,255,255,0.25)",
          mb: 3,
        }}
      >
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: "#4ADE80",
            animation: "pulse 2s infinite",
          }}
        />
        <Typography
          sx={{
            color: "rgba(255,255,255,0.9)",
            fontSize: "0.75rem",
            fontWeight: 600,
            fontFamily: CLINICAL_FONT_FAMILY,
          }}
        >
          Platform Online · 99.9% Uptime
        </Typography>
      </Box>

      {/* Headline */}
      <Typography
        sx={{
          color: "white",
          fontFamily: CLINICAL_FONT_FAMILY,
          fontWeight: 800,
          fontSize: { lg: "2.4rem", xl: "2.8rem" },
          lineHeight: 1.15,
          mb: 2,
          letterSpacing: "-0.03em",
        }}
      >
        Accelerate Your{" "}
        <Box component="span" sx={{ color: "#7FFFD4" }}>
          Clinical Trials
        </Box>
      </Typography>
      <Typography
        sx={{
          color: "rgba(255,255,255,0.75)",
          fontFamily: CLINICAL_FONT_FAMILY,
          fontSize: "1rem",
          lineHeight: 1.6,
          mb: 5,
          maxWidth: 440,
        }}
      >
        A comprehensive AI-assisted CTMS platform designed to streamline study
        lifecycle management, regulatory compliance, and multi-site
        coordination.
      </Typography>

      {/* Feature cards */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 5 }}>
        {FEATURE_LIST.map((feature) => (
          <Box
            key={feature.key}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
              p: 2,
              backgroundColor: "rgba(255,255,255,0.08)",
              borderRadius: 2,
              border: "1px solid rgba(255,255,255,0.12)",
              backdropFilter: "blur(4px)",
              transition: "background 0.2s",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.13)" },
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                backgroundColor: "rgba(255,255,255,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                color: "white",
              }}
            >
              <feature.Icon size={18} />
            </Box>
            <Box>
              <Typography
                sx={{
                  color: "white",
                  fontFamily: CLINICAL_FONT_FAMILY,
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  mb: 0.25,
                }}
              >
                {feature.title}
              </Typography>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.65)",
                  fontFamily: CLINICAL_FONT_FAMILY,
                  fontSize: "0.78rem",
                  lineHeight: 1.5,
                }}
              >
                {feature.desc}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Supported roles */}
      <Box>
        <Typography
          sx={{
            color: "rgba(255,255,255,0.5)",
            fontSize: "0.7rem",
            fontFamily: CLINICAL_FONT_FAMILY,
            fontWeight: 600,
            letterSpacing: "0.08em",
            mb: 1.5,
          }}
        >
          SUPPORTED ROLES
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          {SUPPORTED_ROLES.map((role) => (
            <Chip
              key={role}
              label={role}
              size="small"
              sx={{
                backgroundColor: "rgba(255,255,255,0.12)",
                color: "rgba(255,255,255,0.85)",
                border: "1px solid rgba(255,255,255,0.2)",
                fontSize: "0.7rem",
                height: 24,
                fontFamily: CLINICAL_FONT_FAMILY,
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  </Box>
);

interface LoginFormSectionProps {
  isDark: boolean;
  email: string;
  setEmail: (v: string) => void;
  password: string;
  setPassword: (v: string) => void;
  rememberMe: boolean;
  setRememberMe: (v: boolean) => void;
  showPassword: boolean;
  setShowPassword: (v: boolean) => void;
  loading: boolean;
  error: string | null;
  rolesPendingMessage: string | null;
  onSubmit: (e: React.FormEvent) => void;
  onForgotPassword: () => void;
}

const LoginFormSection: React.FC<LoginFormSectionProps> = ({
  isDark,
  email,
  setEmail,
  password,
  setPassword,
  rememberMe,
  setRememberMe,
  showPassword,
  setShowPassword,
  loading,
  error,
  rolesPendingMessage,
  onSubmit,
  onForgotPassword,
}) => {
  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 1.5,
      backgroundColor: isDark ? "rgba(15,23,42,0.5)" : "#F8FAFC",
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#0B5FFF",
        borderWidth: 2,
      },
    },
    "& input": { fontFamily: CLINICAL_FONT_FAMILY },
  };

  const isServerError =
    error?.includes("no data") ?? error?.includes("No Content");

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        px: { xs: 2, sm: 4, md: 6 },
        py: 4,
        backgroundColor: isDark ? "#0F172A" : "#F8FAFC",
        overflowY: "auto",
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 420 }}>
        {/* Page heading */}
        <Box sx={{ mb: 4, textAlign: "center" }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #0B5FFF 0%, #0FB6A6 100%)",
              mb: 2,
              boxShadow: "0 4px 20px rgba(11,95,255,0.3)",
            }}
          >
            <Activity size={26} color="white" />
          </Box>
          <Typography
            sx={{
              color: isDark ? "#F8FAFC" : "#0F172A",
              fontFamily: CLINICAL_FONT_FAMILY,
              fontWeight: 700,
              fontSize: "1.5rem",
              letterSpacing: "-0.025em",
              mb: 0.5,
            }}
          >
            Welcome back
          </Typography>
          <Typography
            sx={{
              color: isDark ? "#64748B" : "#94A3B8",
              fontFamily: CLINICAL_FONT_FAMILY,
              fontSize: "0.875rem",
            }}
          >
            Sign in to your AIClinTrack account
          </Typography>
        </Box>

        {/* Alerts */}
        {rolesPendingMessage && (
          <Alert
            severity="info"
            sx={{ mb: 2, fontFamily: CLINICAL_FONT_FAMILY }}
          >
            {rolesPendingMessage}
          </Alert>
        )}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2, fontFamily: CLINICAL_FONT_FAMILY }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {isServerError ? "Server Configuration Issue" : "Login Failed"}
            </Typography>
            <Typography variant="body2">{error}</Typography>
            {isServerError && (
              <Typography
                variant="caption"
                component="div"
                sx={{ mt: 0.5, opacity: 0.8 }}
              >
                This usually means the server endpoint is responding but not
                returning the expected login data.
              </Typography>
            )}
          </Alert>
        )}

        {/* Login form card */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${isDark ? "rgba(51,65,85,0.6)" : "#E2E8F0"}`,
            backgroundColor: isDark ? "rgba(30,41,59,0.8)" : "white",
            backdropFilter: "blur(8px)",
            boxShadow: isDark
              ? "0 4px 32px rgba(0,0,0,0.4)"
              : "0 4px 24px rgba(11,95,255,0.06)",
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box
              component="form"
              onSubmit={onSubmit}
              sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
              className="clinical-form"
            >
              {/* Email field */}
              <Box>
                <Typography
                  variant="subtitle2"
                  component="label"
                  sx={{
                    mb: 0.75,
                    display: "block",
                    fontFamily: CLINICAL_FONT_FAMILY,
                    fontWeight: 600,
                    color: isDark ? "#CBD5E1" : "#374151",
                    fontSize: "0.8rem",
                  }}
                >
                  Email Address
                </Typography>
                <TextField
                  fullWidth
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  size="small"
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: (
                      <Box
                        sx={{ mr: 1, display: "flex", alignItems: "center" }}
                      >
                        <Mail size={15} color="#9CA3AF" />
                      </Box>
                    ),
                  }}
                />
              </Box>

              {/* Password field */}
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.75,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    component="label"
                    sx={{
                      fontFamily: CLINICAL_FONT_FAMILY,
                      fontWeight: 600,
                      color: isDark ? "#CBD5E1" : "#374151",
                      fontSize: "0.8rem",
                    }}
                  >
                    Password
                  </Typography>
                  <Typography
                    component="a"
                    href="#"
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      onForgotPassword();
                    }}
                    sx={{
                      color: "#0B5FFF",
                      textDecoration: "none",
                      fontFamily: CLINICAL_FONT_FAMILY,
                      fontSize: "0.78rem",
                      fontWeight: 500,
                      cursor: "pointer",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Forgot password?
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  size="small"
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: (
                      <Box
                        sx={{ mr: 1, display: "flex", alignItems: "center" }}
                      >
                        <Lock size={15} color="#9CA3AF" />
                      </Box>
                    ),
                    endAdornment: (
                      <IconButton
                        onClick={() => {
                          void setShowPassword(!showPassword);
                        }}
                        size="small"
                        edge="end"
                      >
                        {showPassword ? (
                          <EyeOff size={15} color="#9CA3AF" />
                        ) : (
                          <Eye size={15} color="#9CA3AF" />
                        )}
                      </IconButton>
                    ),
                  }}
                />
              </Box>

              {/* Remember me */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={loading}
                    size="small"
                    sx={{
                      color: isDark ? "#475569" : "#D1D5DB",
                      "&.Mui-checked": { color: "#0B5FFF" },
                      p: 0.5,
                    }}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: CLINICAL_FONT_FAMILY,
                      color: isDark ? "#94A3B8" : "#6B7280",
                      fontSize: "0.8rem",
                    }}
                  >
                    Keep me signed in
                  </Typography>
                }
                sx={{ ml: 0 }}
              />

              {/* Submit button */}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                endIcon={!loading && <ArrowRight size={16} />}
                sx={{
                  py: 1.25,
                  fontFamily: CLINICAL_FONT_FAMILY,
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  borderRadius: 1.5,
                  background:
                    "linear-gradient(135deg, #0B5FFF 0%, #0FB6A6 100%)",
                  boxShadow: "0 2px 12px rgba(11,95,255,0.3)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #0047CC 0%, #0A8A7E 100%)",
                    boxShadow: "0 4px 20px rgba(11,95,255,0.4)",
                    transform: "translateY(-1px)",
                  },
                  "&.Mui-disabled": {
                    background: isDark ? "#1E293B" : "#E2E8F0",
                  },
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress
                      size={16}
                      sx={{ mr: 1, color: "inherit" }}
                    />
                    Signing In…
                  </>
                ) : (
                  "Sign In to AIClinTrack"
                )}
              </Button>

              <Divider
                sx={{ borderColor: isDark ? "rgba(51,65,85,0.5)" : "#F1F5F9" }}
              >
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    color: isDark ? "#475569" : "#94A3B8",
                    fontFamily: CLINICAL_FONT_FAMILY,
                    px: 1,
                  }}
                >
                  OR
                </Typography>
              </Divider>

              {/* SSO placeholder */}
              <Button
                disabled
                variant="outlined"
                fullWidth
                sx={{
                  fontFamily: CLINICAL_FONT_FAMILY,
                  borderRadius: 1.5,
                  borderColor: isDark ? "rgba(51,65,85,0.6)" : "#E2E8F0",
                  color: isDark ? "#475569" : "#9CA3AF",
                  "&.Mui-disabled": {
                    borderColor: isDark ? "rgba(51,65,85,0.4)" : "#E2E8F0",
                    color: isDark ? "#334155" : "#CBD5E1",
                  },
                }}
              >
                SSO Login — Coming Soon
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Trust badges */}
        <Box
          sx={{
            mt: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          {TRUST_BADGES.map((badge) => (
            <Box
              key={badge.key}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                color: isDark ? "#475569" : "#94A3B8",
              }}
            >
              <badge.Icon size={13} />
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  fontFamily: CLINICAL_FONT_FAMILY,
                  color: isDark ? "#475569" : "#94A3B8",
                }}
              >
                {badge.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const PageFooter: React.FC<{ isDark: boolean }> = ({ isDark }) => (
  <Box
    sx={{
      py: 1.5,
      px: 3,
      textAlign: "center",
      backgroundColor: isDark ? "rgba(15,23,42,0.9)" : "rgba(255,255,255,0.8)",
      backdropFilter: "blur(8px)",
      borderTop: `1px solid ${isDark ? "rgba(51,65,85,0.3)" : "rgba(11,95,255,0.07)"}`,
    }}
  >
    <Typography
      sx={{
        color: isDark ? "#475569" : "#94A3B8",
        fontSize: "0.72rem",
        fontFamily: CLINICAL_FONT_FAMILY,
      }}
    >
      © {new Date().getFullYear()} AIClinTrack · All rights reserved · Version
      2.0.0 · For authorised clinical trial personnel only
    </Typography>
  </Box>
);

// ─── Main component ────────────────────────────────────────────

interface LoginPageProps {
  onLogin: (user: User, passwordExpiry?: PasswordExpiryInfo) => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLogin }) => {
  const { mode, toggleTheme } = useThemeMode();
  const isDark = mode === "dark";

  const [searchParams, setSearchParams] = useSearchParams();
  const [rolesPendingMessage, setRolesPendingMessage] = useState<string | null>(
    null,
  );
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const {
    email,
    setEmail,
    password,
    setPassword,
    rememberMe,
    setRememberMe,
    showPassword,
    setShowPassword,
    loading,
    error,
    snackbarOpen,
    closeSnackbar,
    handleSubmit,
  } = useLoginForm(onLogin);

  useEffect(() => {
    if (searchParams.get("rolesPending") === "1") {
      setRolesPendingMessage(
        "Registration successful, but your invitation does not include roles. Please ask the tenant administrator to assign roles, then come back and log in.",
      );
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  if (showForgotPassword) {
    return (
      <ForgotPasswordPage onBackToLogin={() => setShowForgotPassword(false)} />
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        fontFamily: CLINICAL_FONT_FAMILY,
        backgroundColor: isDark ? "#0F172A" : "#F0F6FF",
        "@keyframes float": {
          "0%, 100%": { transform: "translateY(0px)" },
          "50%": { transform: "translateY(-12px)" },
        },
        "@keyframes pulse": {
          "0%, 100%": { opacity: 0.6 },
          "50%": { opacity: 1 },
        },
      }}
    >
      <PageTopBar isDark={isDark} toggleTheme={toggleTheme} />

      <Box sx={{ flex: 1, display: "flex", minHeight: "calc(100vh - 60px)" }}>
        <LeftMarketingPanel isDark={isDark} />
        <LoginFormSection
          isDark={isDark}
          email={email}
          setEmail={setEmail}
          password={password}
          setPassword={setPassword}
          rememberMe={rememberMe}
          setRememberMe={setRememberMe}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          loading={loading}
          error={error}
          rolesPendingMessage={rolesPendingMessage}
          onSubmit={handleSubmit}
          onForgotPassword={() => setShowForgotPassword(true)}
        />
      </Box>

      <PageFooter isDark={isDark} />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity="error"
          variant="filled"
          sx={{ fontFamily: CLINICAL_FONT_FAMILY, minWidth: 300 }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LoginPage;
